// ─────────────────────────────────────────────────────────────────
// Glass-i — Account Reports grouping helpers (Stage 6B.9G).
//
// Pure-JS helpers consumed by:
//   - BalanceSheetPage grouped view
//   - ProfitLossPage grouped view
//   - FixedAssetSchedulePage / InvestmentSchedulePage summary cards
//
// No React, no axios, no JSX so node:test can import these directly.
// ─────────────────────────────────────────────────────────────────

function toNumber(v) {
  if (v === null || v === undefined || v === '') return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
}

// Format an INR number with thousand separators and 2dp. Matches the
// existing `formatINR` in accountReportsConstants but defined here too
// so the pure-helper file has no React import path.
export function formatRupee(n) {
  return toNumber(n).toLocaleString('en-IN', {
    minimumFractionDigits: 2, maximumFractionDigits: 2
  });
}

// Wrap-safe extraction of the grouped section structure that the
// backend now emits. Returns `[]` when the backend response is on the
// legacy schema (no grouping) so the UI can fall back to the flat list.
export function safeGroups(section) {
  if (!section || typeof section !== 'object') return [];
  return Array.isArray(section.groups) ? section.groups : [];
}

// Get a friendly label for a group. Prefers the human label resolved
// by the backend; falls back to the account_group; then the
// account_subtype; finally a placeholder.
export function groupLabel(group) {
  if (!group) return '(unknown)';
  return group.accountSubtypeLabel
    || group.accountGroup
    || group.accountSubtype
    || '(unclassified)';
}

// Get a one-line subtitle for a group: `<subtypeLabel> · <accountGroup>`
// when both are set, otherwise just the part that exists.
export function groupSubtitle(group) {
  if (!group) return '';
  const subtype = group.accountSubtypeLabel || group.accountSubtype || null;
  const grp     = group.accountGroup || null;
  if (subtype && grp && subtype.toLowerCase() !== grp.toLowerCase()) return `${subtype} · ${grp}`;
  return subtype || grp || '';
}

// Sum the group totals — useful when the UI wants to compute its
// own running total without trusting an API summary.
export function sumGroupTotals(groups, key = 'total') {
  if (!Array.isArray(groups)) return 0;
  const sum = groups.reduce((s, g) => s + toNumber(g[key]), 0);
  return Math.round((sum + Number.EPSILON) * 100) / 100;
}

// Build a flat row list from grouped sections, preserving group
// boundaries via an injected `__groupHeader__` marker. The schedule
// pages use this when the operator picks "Grouped" rendering.
export function flattenGroupedRows(groups, accountsKey = 'accounts') {
  if (!Array.isArray(groups) || groups.length === 0) return [];
  const out = [];
  for (const g of groups) {
    out.push({ __groupHeader__: true, group: g });
    for (const row of g[accountsKey] || []) out.push(row);
  }
  return out;
}

// ─────────────────────────────────────────────────────────────────
// Schedule helpers (Fixed Asset + Investment).
// ─────────────────────────────────────────────────────────────────

// Map a backend Fixed Asset Schedule row to its computed display
// values (no recomputation — backend already rounds).
export function pickFixedAssetRow(row) {
  return {
    id:                       row.id,
    assetCode:                row.assetCode,
    assetName:                row.assetName,
    categoryName:             row.categoryName,
    categoryCode:             row.categoryCode,
    purchaseDate:             row.purchaseDate,
    capitalizationDate:       row.capitalizationDate,
    originalCost:             toNumber(row.originalCost),
    additionalCost:           toNumber(row.additionalCost),
    totalCost:                toNumber(row.totalCost),
    accumulatedDepreciation:  toNumber(row.accumulatedDepreciation),
    writtenDownValue:         toNumber(row.writtenDownValue),
    status:                   row.status,
    disposalDate:             row.disposalDate,
    saleValue:                row.saleValue,
    gainLossOnDisposal:       row.gainLossOnDisposal
  };
}

export function pickInvestmentRow(row) {
  return {
    id:                  row.id,
    investmentCode:      row.investmentCode,
    investmentName:      row.investmentName,
    categoryName:        row.categoryName,
    investmentClass:     row.investmentClass,
    institutionName:     row.institutionName,
    investmentDate:      row.investmentDate,
    maturityDate:        row.maturityDate,
    principalAmount:     toNumber(row.principalAmount),
    currentValue:        toNumber(row.currentValue),
    accruedIncome:       toNumber(row.accruedIncome),
    units:               row.units,
    interestRate:        row.interestRate,
    status:              row.status,
    disposalDate:        row.disposalDate,
    disposalValue:       row.disposalValue,
    gainLossOnDisposal:  row.gainLossOnDisposal
  };
}

// Status colours used by the schedule pages.
export const FA_STATUS_COLOR = Object.freeze({
  active: 'green', sold: 'blue', scrapped: 'red', retired: 'default'
});
export const FA_STATUS_LABEL = Object.freeze({
  active: 'Active', sold: 'Sold', scrapped: 'Scrapped', retired: 'Retired'
});
export const INV_STATUS_COLOR = Object.freeze({
  active: 'green', matured: 'blue', sold: 'gold', closed: 'default'
});
export const INV_STATUS_LABEL = Object.freeze({
  active: 'Active', matured: 'Matured', sold: 'Sold', closed: 'Closed'
});

// Friendly error envelope (mirrors fixedAssetsHelpers / investmentsHelpers
// behaviour). Reports surface the same `{response.data.error.message}`
// shape from the controller.
export function friendlyReportError(err) {
  if (!err) return 'Something went wrong.';
  const body = err && err.response && err.response.data && err.response.data.error
    ? err.response.data.error : null;
  return (body && body.message) || err.message || 'Request failed.';
}

// Build axios-friendly query params, stripping null / undefined / empty
// strings. Mirrors `cleanParams` in accountReportsApi.js.
export function cleanQueryParams(params) {
  const out = {};
  if (!params) return out;
  for (const [k, v] of Object.entries(params)) {
    if (v === null || v === undefined || v === '') continue;
    out[k] = v;
  }
  return out;
}
